import { useSidebarPinState } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import { useReaderToc } from './useReaderToc';

/*
 * The reader's right-hand "On this page" rail.
 *
 * Headings and the active one come from `useReaderToc`; this file is only the
 * desktop presentation. Below the phone breakpoint the rail's column is gone
 * from the reader altogether (`readerStyles` hides both sidebars) and
 * `DocsNavAddon` carries the same state in its sheet instead, so the rail stands
 * down rather than rendering into a hidden column.
 */
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  heading: {
    padding: '0 12px 8px 12px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: 'var(--hone-portal-fg-tertiary)',
  },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    // 28px is the rail's row rhythm; a heading that wraps grows instead of
    // clipping, which several real doc headings do at 224px.
    minHeight: 28,
    padding: '4px 0',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
    color: 'var(--bui-fg-secondary)',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--bui-fg-primary)',
    },
  },
  itemActive: {
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 5,
      bottom: 5,
      width: 3,
      borderRadius: 2,
      background: '#f8f93f',
    },
  },
});

export const OnThisPageAddon = () => {
  const classes = useStyles();
  const { isMobile } = useSidebarPinState();
  const { headings, activeId, jumpTo } = useReaderToc();

  if (isMobile || headings.length === 0) return null;

  return (
    <nav className={classes.root} aria-label="On this page">
      <span className={classes.heading}>On this page</span>
      {headings.map(heading => (
        <button
          key={heading.id}
          type="button"
          className={`${classes.item} ${
            heading.id === activeId ? classes.itemActive : ''
          }`}
          style={{ paddingLeft: heading.depth === 2 ? 24 : 12 }}
          onClick={() => jumpTo(heading.id)}
        >
          {heading.label}
        </button>
      ))}
    </nav>
  );
};
